import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Menu, Segment, Input, Button, Modal } from 'semantic-ui-react';
import i18next from 'i18next';
import { DataGrid } from '../common';
import { getCurrentRestaurant, getCurrentChain } from '../../selectors';
import { fetchLocales, updateLocales, createLocale } from '../../actions';
import { hasSuperPermission } from '../../helpers/permissions';

const namespaces = ['kiosk', 'website'];

const SystemLocales = (props) => {
    const dispatch = useDispatch();
    const [namespace, setNamespace] = useState(namespaces[0]);
    const [locales, setLocales] = useState([]);
    const [overridesOnly, toggleOverridesOnly] = useState(false);
    const [overrides, setOverrides] = useState({ chainOverrides: {}, branchOverrides: {} });
    const [newRow, setNewRow] = useState(null);
    const [updates, updateRow] = useState(JSON.parse(sessionStorage.getItem('localesTempUpdates') || '{}'));
    const chain = useSelector(getCurrentChain);
    const restaurant = useSelector(getCurrentRestaurant);
    const isMaster = !chain && !restaurant;
    const languages = locales.length ? Object.keys(locales[0]).filter(key => key !== 'key' && key !== 'ns') : [];

    const { id: branchId, ownerId } = restaurant || {};
    const chainId = chain ? chain.id : ownerId;

    useEffect(() => {
        dispatch(fetchLocales(namespace, chainId, branchId, ({ locales, ...overrides }) => {
            setLocales(locales);
            setOverrides(overrides);
        }));
    }, [namespace, chainId, branchId]);

    useEffect(() => {
        sessionStorage.setItem('localesTempUpdates', JSON.stringify(updates));
    }, [updates]);

    const saveUpdates = () => {
        dispatch(updateLocales(namespace, chain ? chain.id : null, restaurant ? restaurant.id : null, updates, result => {
            updateRow({});
            if (result.overrides) setOverrides({ ...overrides, ...result.overrides });
            if (result.locales) setLocales(result.locales);
        }));
    };

    const saveNewRow = () => {
        dispatch(createLocale(namespace, newRow, result => {
            setLocales([result.newRow].concat(locales));
            setNewRow(null);
        }));
    };

    const renderTable = (data, createMode = false) => {
        const columns = [{
            Header: 'Key',
            accessor: 'key',
            filterable: !createMode,
            Cell: cellInfo => {
                const row = cellInfo.original;

                if (createMode) {
                    return (
                        <Input fluid value={row.key} onChange={({ target }) => setNewRow({ ...newRow, key: String(target.value).replace(/\s/g,'') })} />
                    );
                }

                return row.key;
            }
        }].concat(languages.map(lng => ({
            Header: String(lng).toUpperCase(),
            accessor: lng,
            filterable: !createMode,
            // eslint-disable-next-line react/display-name
            Cell: cellInfo => {
                const row = cellInfo.original;

                let label = null;
                if (row.updatedLngs && row.updatedLngs.includes(lng)) {
                    label = {
                        icon: 'close', color: 'teal', onClick: () => {
                            if (Object.keys(updates[row.key]).length > 1) {
                                const { [lng]: x, ...clearedUpdates } = updates[row.key];
                                updateRow({ ...updates, [row.key]: clearedUpdates });
                            } else {
                                const { [row.key]: x, ...clearedUpdates } = updates;
                                updateRow({ ...clearedUpdates });
                            }
                        }
                    };
                } else if (row.overridedLngs && row.overridedLngs.includes(lng)) {
                    label = {
                        icon: 'undo', color: 'orange', onClick: () => {
                            updateRow({ ...updates, [row.key]: { ...updates[row.key], [lng]: null } });
                        }
                    };
                }

                return (
                    <Input
                        fluid
                        value={row[lng] || ''}
                        placeholder={row.originalValues && row.originalValues[lng] || ''}
                        label={label}
                        labelPosition='right corner'
                        style={{ direction: ['he', 'ar'].includes(lng) ? 'rtl' : 'ltr' }}
                        onChange={({ target }) => {
                            if (createMode) {
                                setNewRow({ ...newRow, [lng]: target.value });
                            } else {
                                updateRow({ ...updates, [row.key]: { ...updates[row.key], [lng]: target.value } });
                            }
                        }}
                    />
                );
            }
        })));

        return (
            <DataGrid
                data={data}
                columns={columns}
                hidePanel={createMode}
                paginated={!createMode}
                pageSize={createMode ? 1 : 20}
               
                extraActions={createMode ? null : renderAction()}
                extraInfo={createMode ? null : renderPicker()}
            />
        );
    };

    const renderPicker = () => {
        if (isMaster) return null;

        return (
            <div style={{ marginLeft: 20, marginRight: 20 }}>
                <Button toggle active={overridesOnly} onClick={() => toggleOverridesOnly(!overridesOnly)}>
                    {i18next.t('filterOverrides')}
                </Button>
            </div>
        );
    };

    const renderAction = () => {
        if (Object.keys(updates).length) {
            return (
                <Button
                    floated="right"
                    primary
                    onClick={saveUpdates}
                    style={{ marginLeft: 10, marginRight: 10, width: 100 }}
                >
                    {i18next.t('save')}
                </Button>
            );
        }

        if (hasSuperPermission('developer') && isMaster) {
            return (
                <Modal
                    trigger={
                        <Button
                            icon
                            floated="right"
                            basic
                            onClick={() => setNewRow({ key: '', ...languages.reduce((lngs, lng) => ({ ...lngs, [lng]: '' }), {}) })}
                            style={{ marginLeft: 10, marginRight: 10, width: 100 }}
                        >
                            {i18next.t('create')}
                        </Button>
                    }
                    open={!!newRow}
                    onClose={() => setNewRow(null)}
                >
                    <Modal.Header>{i18next.t('create')}</Modal.Header>
                    <Modal.Content style={{ direction: i18next.dir() }}>
                        <Modal.Description>
                            {renderTable([newRow], true)}
                        </Modal.Description>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button onClick={() => setNewRow(null)}>{i18next.t('cancel')}</Button>
                        <Button
                            positive
                            icon='checkmark'
                            labelPosition='right'
                            content={i18next.t('save')}
                            disabled={!newRow || !newRow.key}
                            onClick={() => saveNewRow(newRow)}
                        />
                    </Modal.Actions>
                </Modal>
            );
        }
    };

    return (
        <div style={{ padding: 10 }}>
            <Menu pointing>
                {namespaces.map(ns => (<Menu.Item key={ns} name={String(ns).toUpperCase()} active={namespace === ns} onClick={() => setNamespace(ns)} />))}
            </Menu>

            <Segment>
                {renderTable(
                    locales
                        .map(row => {
                            const { chainOverrides, branchOverrides } = overrides;

                            return languages.reduce((final, lng) => {
                                const originalValue = final[lng];
                                const chainValue = (chainOverrides[row.key] || {})[lng] || undefined;
                                const branchValue = restaurant && (branchOverrides[row.key] || {})[lng] || undefined;
                                const updateValue = (updates[row.key] || {})[lng];

                                const selectedValue = [updateValue, branchValue, chainValue, originalValue].find(v => v !== undefined);
                                const overridedLngs = isMaster ? [] : (final.overridedLngs || []).concat(selectedValue === (chain ? chainValue : branchValue) ? lng : []);
                                const updatedLngs = (final.updatedLngs || []).concat(selectedValue === updateValue ? lng : []);
                                const originalValues = { ...final.originalValues, [lng]: originalValue };

                                return { ...final, [lng]: selectedValue, overridedLngs, updatedLngs, originalValues };
                            }, row);
                        })
                        .filter(row => !overridesOnly || row.overridedLngs.length || row.updatedLngs.length)
                )}
            </Segment>
        </div>
    );
};

SystemLocales.propTypes = {
};

export default SystemLocales;